'use client';

import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, CheckSquare, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  WizardPanelBody,
  WizardPanelCard,
  WizardPanelHeader,
  SectionHeader,
  Field,
  CollapsibleSection,
  inputClass,
  textareaClass,
} from '@/components/event-wizard/wizard-ui';
import { ToggleRow } from '@/components/event-wizard/wizard-form-controls';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import {
  emptyTicketForm,
  type TicketFieldErrors,
  type TicketFormState,
} from '@/components/event-wizard/ticket-wizard-utils';
import type { RegistrationFieldRecord } from '@/components/event-wizard/registration-builder-utils';
import {
  connectableFormFieldId,
  connectedFieldErrorById,
  connectedFieldsSummary,
  hasHighlightableTicketErrors,
  orphanConnectedFieldErrors,
  parseConnectedFormFieldErrors,
} from '@/components/event-wizard/connected-form-fields';
import { activityApi, type ActivityFrameworkCatalogItem, type EventActivityRecord } from '@/lib/api';

type Props = {
  eventId?: string | null;
  registrationFields?: RegistrationFieldRecord[];
  tickets: Array<Record<string, any>>;
  ticketForm: TicketFormState;
  onTicketFormChange: (form: TicketFormState) => void;
  onAddTicket: () => void;
  onDeleteTicket: (ticketId: string) => void;
  onEditTicket?: (ticket: Record<string, any>) => void;
  onMoveTicket?: (ticketId: string, direction: 'up' | 'down') => void;
  onCancelEdit?: () => void;
  editingTicketId?: string | null;
  fieldErrors?: TicketFieldErrors & Record<string, string>;
  saving: boolean;
  isPublished?: boolean;
};

export function TicketsWizardPanel({
  eventId = null,
  registrationFields = [],
  tickets,
  ticketForm,
  onTicketFormChange,
  onAddTicket,
  onDeleteTicket,
  onEditTicket,
  onMoveTicket,
  onCancelEdit,
  editingTicketId = null,
  fieldErrors = {},
  saving,
  isPublished = false,
}: Props) {
  const [deletingTicketId, setDeletingTicketId] = useState<string | null>(null);
  const patch = (partial: Partial<TicketFormState>) => onTicketFormChange({ ...ticketForm, ...partial });
  const patchComm = (partial: Partial<TicketFormState['communication']>) =>
    onTicketFormChange({ ...ticketForm, communication: { ...ticketForm.communication, ...partial } });
  const parsePriceInput = (value: string) => Number(value.replace(/^0+(?=\d)/, '')) || 0;
  const connectedErrors = parseConnectedFormFieldErrors(fieldErrors, ticketForm.form_fields);
  const orphanErrors = orphanConnectedFieldErrors(ticketForm.form_fields, registrationFields, connectedErrors);
  const connectedSummary = connectedFieldsSummary(connectedErrors);
  const showHighlightedSummary = hasHighlightableTicketErrors(fieldErrors, [
    'name',
    'price',
    'promo_code',
    'quantity',
    'min_per_order',
    'max_per_order',
    'max_per_user',
    'sale_start',
    'sale_end',
    'sold_out_behavior',
    'metadata_json',
  ]);

  const [activities, setActivities] = useState<EventActivityRecord[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [editingActivityName, setEditingActivityName] = useState('');
  const [renamingLoading, setRenamingLoading] = useState(false);

  const [catalog, setCatalog] = useState<ActivityFrameworkCatalogItem[]>([]);
  const [newActivityOpen, setNewActivityOpen] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityFramework, setNewActivityFramework] = useState('');
  const [creatingActivity, setCreatingActivity] = useState(false);

  const startRenameActivity = (id: string, currentName: string) => {
    setEditingActivityId(id);
    setEditingActivityName(currentName);
  };

  const saveRenameActivity = async (activityId: string) => {
    if (!editingActivityName.trim()) return;
    setRenamingLoading(true);
    try {
      const res = await activityApi.update(activityId, { name: editingActivityName.trim() });
      const updated = res.data?.data;
      setActivities((prev) =>
        prev.map((a) =>
          String(a.id) === activityId ? { ...a, ...(updated || {}), name: editingActivityName.trim() } : a
        )
      );
      setEditingActivityId(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to rename activity');
    } finally {
      setRenamingLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    activityApi.catalog().then((res) => {
      if (cancelled) return;
      const raw = res.data?.data ?? [];
      const list = raw.filter((item: any) => item.key !== 'coding_contest');
      setCatalog(list);
      if (list.length > 0) {
        setNewActivityFramework(list[0].key);
      }
    }).catch(() => {
      if (!cancelled) setCatalog([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!eventId) {
      setActivities([]);
      return;
    }
    let cancelled = false;
    setActivitiesLoading(true);
    activityApi
      .list(eventId)
      .then((res) => {
        if (cancelled) return;
        setActivities(Array.isArray(res.data?.data?.activities) ? res.data.data.activities : []);
      })
      .catch(() => {
        if (!cancelled) setActivities([]);
      })
      .finally(() => {
        if (!cancelled) setActivitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const quickCreateActivity = async () => {
    if (!eventId || creatingActivity) return;
    const framework_key = newActivityFramework || catalog[0]?.key || 'mun';
    const frameworkLabel = catalog.find((c) => c.key === framework_key)?.label || 'New Activity';
    if (activities.some((a) => String(a.framework_key || '').toLowerCase() === framework_key.toLowerCase())) {
      alert(`${frameworkLabel} activity already exists for this event.`);
      return;
    }
    const name = newActivityName.trim() || frameworkLabel;
    setCreatingActivity(true);
    try {
      const res = await activityApi.create(eventId, { framework_key, name });
      const created = res.data?.data;
      if (created) {
        setActivities((prev) => [...prev, created]);
        const nextIds = Array.from(new Set([...ticketForm.activity_ids, String(created.id)]));
        const primary = ticketForm.event_activity_id || String(created.id);
        patch({ activity_ids: nextIds, event_activity_id: primary });
        setNewActivityName('');
        setNewActivityOpen(false);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create activity');
    } finally {
      setCreatingActivity(false);
    }
  };

  const toggleActivity = (activityId: string, checked: boolean) => {
    const currentIds = ticketForm.activity_ids ?? [];
    const nextIds = checked
      ? Array.from(new Set([...currentIds, activityId]))
      : currentIds.filter((id) => id !== activityId);
    let primary = ticketForm.event_activity_id ?? '';
    if (primary && !nextIds.includes(primary)) {
      primary = nextIds[0] ?? '';
    } else if (!primary && checked && nextIds.length === 1) {
      primary = activityId;
    }

    // When 2 or more activities are selected, deduplicate common fields
    let updatedFormFields = ticketForm.form_fields;
    if (nextIds.length >= 2 && Array.isArray(ticketForm.form_fields) && ticketForm.form_fields.length > 0) {
      const seen = new Set<string>();
      updatedFormFields = ticketForm.form_fields.filter((f) => {
        const fieldObj = registrationFields.find(
          (rf) => rf.id === f.form_field_id || (rf as any).form_field_id === f.form_field_id
        );
        const slug = (fieldObj?.slug || (f as any).field?.slug || '').toLowerCase().trim();
        const label = (fieldObj?.label || (f as any).field?.label || '').toLowerCase().trim();
        const key = slug || label || f.form_field_id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    patch({ activity_ids: nextIds, event_activity_id: primary, form_fields: updatedFormFields });
  };

  // For normal tickets without activities: auto-link all connectable basic registration form fields by default
  useEffect(() => {
    const isNormalTicket = (ticketForm.activity_ids ?? []).length === 0 && !ticketForm.event_activity_id;
    if (isNormalTicket && ticketForm.form_fields.length === 0 && registrationFields.length > 0) {
      const isBasicField = (f: RegistrationFieldRecord) => {
        if (f.is_system) return true;
        const norm = (f.label || f.slug || '').toLowerCase();
        if (
          norm.includes('committee') ||
          norm.includes('portfolio') ||
          norm.includes('mun') ||
          norm.includes('delegate') ||
          norm.includes('delegation') ||
          norm.includes('github') ||
          norm.includes('tech stack') ||
          norm.includes('hackathon') ||
          norm.includes('grade') ||
          norm.includes('dietary')
        ) {
          return false;
        }
        return ['name', 'email', 'phone', 'college', 'institution', 'organization', 'city'].some((k) =>
          norm.includes(k)
        );
      };

      const connectable = registrationFields
        .filter((f) => Boolean(connectableFormFieldId(f)) && isBasicField(f))
        .map((field, idx) => ({
          form_field_id: connectableFormFieldId(field)!,
          required: Boolean(field.required),
          sort_order: idx,
        }));
      if (connectable.length > 0) {
        patch({ form_fields: connectable });
      }
    }
  }, [ticketForm.activity_ids, ticketForm.event_activity_id, registrationFields]);

  return (
    <div className="flex flex-col gap-4">
      {tickets.length > 0 && (
        <div className="flex flex-col gap-3">
          {tickets.map((t, index) => (
            <WizardPanelCard
              key={t.id}
              className={editingTicketId === t.id ? 'ring-2 ring-[rgba(152,170,157,0.35)]' : undefined}
            >
              <div className="flex items-start justify-between gap-4 px-5 py-4 sm:px-6">
                <div>
                  <p className="font-semibold text-[var(--color-text)]">
                    {t.name}
                    {editingTicketId === t.id && (
                      <span className="ew-badge ew-badge--ok ml-2">Editing</span>
                    )}
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    {Number(t.price) === 0 ? 'Free' : `₹${t.price}`} · qty {t.quantity_available ?? t.quantity}
                    {t.category?.name ? ` · ${t.category.name}` : ''}
                    {Array.isArray(t.activity_ids) && t.activity_ids.length > 0
                      ? ` · ${t.activity_ids.length} activit${t.activity_ids.length === 1 ? 'y' : 'ies'}`
                      : t.activity?.name
                        ? ` · ${t.activity.name}`
                        : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {onMoveTicket && tickets.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => onMoveTicket(String(t.id), 'up')}
                        disabled={saving || index === 0}
                        className="rounded-lg p-1.5 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-muted)] disabled:opacity-30"
                        aria-label="Move ticket up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onMoveTicket(String(t.id), 'down')}
                        disabled={saving || index === tickets.length - 1}
                        className="rounded-lg p-1.5 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-muted)] disabled:opacity-30"
                        aria-label="Move ticket down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {onEditTicket && (
                    <button
                      type="button"
                      onClick={() => onEditTicket(t)}
                      className="rounded-lg p-1.5 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-muted)]"
                      aria-label="Edit ticket"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={saving || deletingTicketId === t.id}
                    onClick={async () => {
                      if (saving || deletingTicketId === t.id) return;
                      setDeletingTicketId(t.id);
                      try {
                        await onDeleteTicket(t.id);
                      } finally {
                        setDeletingTicketId(null);
                      }
                    }}
                    className="rounded-lg p-1.5 text-soot transition-colors hover:bg-plaster/80 disabled:opacity-50"
                    aria-label="Delete ticket"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </WizardPanelCard>
          ))}
        </div>
      )}

      <WizardPanelCard>
        <WizardPanelHeader
          embedded
          title="Tickets"
          description="Optional. Add free or paid tickets to enable public registration. Skip to leave registration disabled."
        />
        <WizardPanelBody className="space-y-4">
          <SectionHeader
            title={editingTicketId ? 'Edit ticket' : 'New ticket'}
            description={editingTicketId ? 'Update this ticket tier and save your changes.' : 'Configure pricing, limits, and registration options.'}
          />
          <div className="space-y-4">
          {fieldErrors.ticket && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
              {fieldErrors.ticket}
            </div>
          )}
          <CollapsibleSection title="Basic ticket details" description="Name, category, visibility, and optional color badge." defaultOpen>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Ticket name" required error={fieldErrors.name} hint="Shown to attendees during registration.">
                <input className={inputClass} value={ticketForm.name} onChange={(e) => patch({ name: e.target.value })} />
              </Field>
              <Field label="Ticket category" hint="Group tickets on the event page (e.g. Early bird).">
                <input className={inputClass} value={ticketForm.category_name} onChange={(e) => patch({ category_name: e.target.value })} />
              </Field>
              <Field label="Description" hint="Optional details shown beside the ticket.">
                <input className={inputClass} value={ticketForm.description} onChange={(e) => patch({ description: e.target.value })} />
              </Field>
              <Field
                label="Visibility"
                hint="Public tickets appear in listings; hidden/invite-only need direct access."
              >
                <select
                  className={inputClass}
                  value={ticketForm.visibility}
                  onChange={(e) => patch({ visibility: e.target.value as TicketFormState['visibility'] })}
                >
                  <option value="public">Public</option>
                  <option value="hidden">Hidden</option>
                  <option value="invite_only">Invite only</option>
                </select>
              </Field>
              <Field label="Ticket color badge" hint="Optional hex color for UI badges (e.g. #4F46E5).">
                <input className={inputClass} value={ticketForm.color_badge} onChange={(e) => patch({ color_badge: e.target.value })} placeholder="#4F46E5" />
              </Field>
              <Field
                label="Price (INR)"
                error={fieldErrors.price}
                hint="Set 0 for free tickets."
              >
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={ticketForm.price}
                  onFocus={(e) => {
                    if (Number(e.currentTarget.value) === 0) e.currentTarget.select();
                  }}
                  onChange={(e) => patch({ price: parsePriceInput(e.target.value) })}
                />
              </Field>

              <Field label="Promo code" error={fieldErrors.promo_code} hint="Optional promo code for discounts.">
                <input className={inputClass} value={ticketForm.promo_code} onChange={(e) => patch({ promo_code: e.target.value })} placeholder="EARLYBIRD" />
              </Field>
              <Field label="Discount percentage" hint="Percentage off when the promo code is applied.">
                <div className="relative">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className={`${inputClass} pr-8`}
                    value={ticketForm.discount_percentage}
                    onChange={(e) => patch({ discount_percentage: Number(e.target.value) })}
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-[var(--color-text-muted)] font-medium">%</span>
                  </div>
                </div>
              </Field>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Connected activities"
            description="Optional. Link this ticket to one or more activities. Existing single-activity tickets keep working."
            defaultOpen
          >
            {!eventId ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                Save the event draft first to connect activities.
              </p>
            ) : activitiesLoading ? (
              <p className="text-sm text-[var(--color-text-muted)]">Loading activities…</p>
            ) : (
              <div className="space-y-4">
                {(() => {
                  if (activities.length === 0) {
                    return (
                      <div className="rounded-xl border border-dashed border-nature-border p-4 text-center">
                        <p className="text-sm text-[var(--color-text-muted)]">
                          No activities created for this event yet.
                        </p>
                        {!newActivityOpen && (
                          <button
                            type="button"
                            onClick={() => setNewActivityOpen(true)}
                            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#5F6F52] px-3.5 py-2 text-xs font-bold text-white transition hover:bg-[#4E5C43]"
                          >
                            <Plus className="h-4 w-4" />
                            Create first activity
                          </button>
                        )}
                      </div>
                    );
                  }

                  const allSelected = activities.length > 0 && activities.every((a) => (ticketForm.activity_ids ?? []).includes(String(a.id)));

                  return (
                    <div className="space-y-3">
                      {activities.length > 1 && (
                        <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)] px-1">
                          <span>{activities.length} activities available</span>
                          <button
                            type="button"
                            onClick={() => {
                              if (allSelected) {
                                patch({ activity_ids: [], event_activity_id: '' });
                              } else {
                                const allIds = activities.map((a) => String(a.id));
                                patch({
                                  activity_ids: allIds,
                                  event_activity_id: ticketForm.event_activity_id || allIds[0] || '',
                                });
                              }
                            }}
                            className="font-semibold text-[#5F6F52] hover:text-[#4E5C43] transition-colors"
                          >
                            {allSelected ? 'Deselect all' : 'Select all'}
                          </button>
                        </div>
                      )}

                      <div className="space-y-2">
                        {activities.map((activity) => {
                          const activityIdStr = String(activity.id);
                          const checked = (ticketForm.activity_ids ?? []).includes(activityIdStr);
                          const isPrimary = ticketForm.event_activity_id === activityIdStr;
                          const isEditing = editingActivityId === activityIdStr;
                          const frameworkLabel = catalog.find(
                            (c) => c.key.toLowerCase() === String(activity.framework_key || '').toLowerCase()
                          )?.label || activity.framework_key;

                          return (
                            <div
                              key={activity.id}
                              className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition-all ${
                                checked
                                  ? 'border-[#5F6F52]/60 bg-[#5F6F52]/5 shadow-sm'
                                  : 'border-nature-border/70 bg-bg hover:bg-plaster/30'
                              }`}
                            >
                              {isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <input
                                    type="text"
                                    value={editingActivityName}
                                    onChange={(e) => setEditingActivityName(e.target.value)}
                                    className={`${inputClass} !py-1.5 text-sm flex-1`}
                                    placeholder="Enter activity name"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        saveRenameActivity(activityIdStr);
                                      } else if (e.key === 'Escape') {
                                        setEditingActivityId(null);
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    disabled={renamingLoading}
                                    onClick={() => saveRenameActivity(activityIdStr)}
                                    className="px-3 py-1.5 bg-[#5F6F52] text-white text-xs font-semibold rounded-lg hover:bg-[#4E5C43] transition-colors disabled:opacity-50 shrink-0"
                                  >
                                    {renamingLoading ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingActivityId(null)}
                                    className="px-2.5 py-1.5 border border-nature-border text-xs font-medium rounded-lg text-soot hover:bg-plaster shrink-0"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <label className="flex cursor-pointer items-center gap-3 min-w-0 flex-1">
                                    <input
                                      type="checkbox"
                                      className="mt-0.5 rounded border-nature-border text-[#5F6F52] focus:ring-[#5F6F52]"
                                      checked={checked}
                                      onChange={(e) => toggleActivity(activityIdStr, e.target.checked)}
                                    />
                                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                                      <span className="font-medium text-soot truncate">{activity.name}</span>
                                      {frameworkLabel && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-plaster text-[var(--color-text-muted)] border border-nature-border/50">
                                          {frameworkLabel}
                                        </span>
                                      )}
                                      {checked && isPrimary && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-[#5F6F52]/20 text-[#5F6F52] border border-[#5F6F52]/30">
                                          Primary
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {checked && !isPrimary && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          patch({ event_activity_id: activityIdStr });
                                        }}
                                        className="text-xs font-medium text-[var(--color-text-muted)] hover:text-[#5F6F52] px-2 py-1 rounded hover:bg-plaster transition-colors"
                                        title="Make primary activity for this ticket"
                                      >
                                        Make Primary
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        startRenameActivity(activityIdStr, activity.name);
                                      }}
                                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5F6F52] hover:text-[#4E5C43] px-2.5 py-1 rounded-lg hover:bg-[#5F6F52]/10 transition-colors"
                                      title="Rename activity"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                      Rename
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {activities.length > 0 && !newActivityOpen && (
                  <button
                    type="button"
                    onClick={() => {
                      const available = catalog.find(
                        (cat) => !activities.some((a) => String(a.framework_key || '').toLowerCase() === cat.key.toLowerCase())
                      );
                      if (available) {
                        setNewActivityFramework(available.key);
                      }
                      setNewActivityOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#5F6F52] hover:text-[#4E5C43] transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add another activity
                  </button>
                )}

                {newActivityOpen && (
                  <div className="rounded-xl border border-[#5F6F52]/30 bg-[#5F6F52]/5 p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#5F6F52]">Create New Activity</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Activity Type" hint="Framework template">
                        <select
                          className={inputClass}
                          value={newActivityFramework}
                          onChange={(e) => setNewActivityFramework(e.target.value)}
                        >
                          {catalog.length === 0 ? (
                            <option value="mun">MUN</option>
                          ) : (
                            catalog.map((cat) => {
                              const isAdded = activities.some(
                                (a) => String(a.framework_key || '').toLowerCase() === cat.key.toLowerCase()
                              );
                              return (
                                <option key={cat.key} value={cat.key} disabled={isAdded}>
                                  {cat.label} ({cat.key}){isAdded ? ' — Already added' : ''}
                                </option>
                              );
                            })
                          )}
                        </select>
                      </Field>
                      <Field label="Activity Name" hint="e.g. Main Track / Committee A">
                        <input
                          className={inputClass}
                          placeholder={catalog.find((c) => c.key === newActivityFramework)?.label || 'New Activity'}
                          value={newActivityName}
                          onChange={(e) => setNewActivityName(e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setNewActivityOpen(false)}
                        className="px-3 py-1.5 border border-nature-border text-xs font-semibold rounded-lg text-soot hover:bg-plaster"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={creatingActivity}
                        onClick={quickCreateActivity}
                        className="px-3 py-1.5 bg-[#5F6F52] text-white text-xs font-semibold rounded-lg hover:bg-[#4E5C43] transition-colors disabled:opacity-50"
                      >
                        {creatingActivity ? 'Creating...' : 'Create & Connect'}
                      </button>
                    </div>
                  </div>
                )}

                {activities.length > 0 && (
                  <Field
                    label="Primary activity"
                    hint="Used for legacy compatibility and recommended form fields. Defaults to the first selected activity."
                  >
                    <select
                      className={inputClass}
                      value={ticketForm.event_activity_id}
                      onChange={(e) => patch({ event_activity_id: e.target.value })}
                      disabled={(ticketForm.activity_ids ?? []).length === 0}
                    >
                      <option value="">None</option>
                      {(ticketForm.activity_ids ?? []).map((id) => {
                        const activity = activities.find((a) => String(a.id) === id);
                        return (
                          <option key={id} value={id}>
                            {activity?.name ?? id}
                          </option>
                        );
                      })}
                    </select>
                  </Field>
                )}
              </div>
            )}
          </CollapsibleSection>

          <CollapsibleSection
            title="Connected form fields"
            description="Select which registration form fields apply to this ticket."
            defaultOpen
          >
            {(ticketForm.activity_ids ?? []).length >= 2 && (
              <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-2.5 text-xs text-emerald-800">
                <CheckSquare className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>
                  <strong>Multi-activity ticket:</strong> Common registration fields across selected activities are automatically consolidated without duplicates and will be auto-filled for attendees.
                </span>
              </div>
            )}
            {(ticketForm.activity_ids ?? []).length === 0 && !ticketForm.event_activity_id && (
              <div className="mb-3 flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50/80 px-4 py-2.5 text-xs text-sky-800">
                <CheckSquare className="h-4 w-4 text-sky-600 shrink-0" />
                <span>
                  <strong>Normal ticket:</strong> Registration form fields are linked and will be presented during ticket purchase.
                </span>
              </div>
            )}
            {connectedSummary ? (
              <p
                role="alert"
                className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {connectedSummary}
              </p>
            ) : null}
            {registrationFields.length === 0 && orphanErrors.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                No custom form fields created. Add them in the Registration form step.
              </p>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const connectableFields = registrationFields.filter((f) => Boolean(connectableFormFieldId(f)));
                  const allFieldsSelected =
                    connectableFields.length > 0 &&
                    connectableFields.every((field) => {
                      const fieldId = connectableFormFieldId(field);
                      return ticketForm.form_fields.some(
                        (f) => (Boolean(fieldId) && f.form_field_id === fieldId) || f.form_field_id === field.id
                      );
                    });

                  if (connectableFields.length === 0) return null;

                  return (
                    <div className="flex items-center justify-between gap-2 rounded-xl border border-nature-border/50 bg-[#FAF8F2] px-3.5 py-2.5 text-xs">
                      <span className="font-medium text-[var(--color-text-muted)]">
                        {ticketForm.form_fields.length} of {connectableFields.length} field{connectableFields.length === 1 ? '' : 's'} connected
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          if (allFieldsSelected) {
                            patch({ form_fields: [] });
                          } else {
                            const updated = connectableFields.map((field, idx) => {
                              const fieldId = connectableFormFieldId(field)!;
                              const existing = ticketForm.form_fields.find(
                                (f) => f.form_field_id === fieldId || f.form_field_id === field.id
                              );
                              return {
                                form_field_id: fieldId,
                                required: existing ? existing.required : (field.required ?? false),
                                sort_order: idx,
                              };
                            });
                            patch({ form_fields: updated });
                          }
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-bg px-2.5 py-1 text-xs font-semibold text-soot shadow-2xs transition hover:bg-mist active:scale-[0.98]"
                      >
                        <CheckSquare className="h-3.5 w-3.5 text-moss" />
                        <span>{allFieldsSelected ? 'Deselect all' : 'Select all'}</span>
                      </button>
                    </div>
                  );
                })()}
                {registrationFields.map((field) => {
                  const fieldId = connectableFormFieldId(field);
                  const matchesField = (f: {
                    form_field_id?: string;
                    field?: { slug?: string; label?: string };
                  }) =>
                    (Boolean(fieldId) && f.form_field_id === fieldId) ||
                    f.form_field_id === field.id ||
                    (Boolean(f.field?.slug) && f.field?.slug === field.slug) ||
                    (Boolean(f.field?.label) &&
                      (f.field?.label || '').trim().toLowerCase() === (field.label || '').trim().toLowerCase());
                  const existing = ticketForm.form_fields.find(matchesField);
                  const checked = !!existing;
                  const payloadIndex = ticketForm.form_fields.findIndex(matchesField);
                  const errorKey = payloadIndex >= 0 ? `form_fields.${payloadIndex}.form_field_id` : undefined;
                  const rowError = connectedFieldErrorById(connectedErrors, fieldId);
                  const canConnect = Boolean(fieldId);

                  return (
                    <div
                      key={field.id}
                      data-field={errorKey}
                      data-error-key={errorKey}
                      data-form-field-id={fieldId ?? undefined}
                      aria-invalid={rowError ? true : undefined}
                      className={`rounded-xl border bg-bg px-4 py-3 text-sm ${
                        rowError ? 'border-red-400 ring-1 ring-red-200' : 'border-nature-border/70'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <label className={`flex items-start gap-3 ${canConnect ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            disabled={!canConnect}
                            aria-describedby={rowError ? `connected-field-error-${field.id}` : undefined}
                            onChange={(e) => {
                              if (!fieldId) return;
                              if (e.target.checked) {
                                patch({
                                  form_fields: [
                                    ...ticketForm.form_fields,
                                    { form_field_id: fieldId, required: field.required ?? false, sort_order: ticketForm.form_fields.length },
                                  ],
                                });
                              } else {
                                patch({
                                  form_fields: ticketForm.form_fields.filter((f) => !matchesField(f)),
                                });
                              }
                            }}
                          />
                          <div>
                            <div className="font-medium text-soot">{field.label}</div>
                            {field.type && (
                              <div className="text-xs text-[var(--color-text-muted)]">
                                Type: {field.type}
                              </div>
                            )}
                            {!canConnect ? (
                              <div className="mt-1 text-xs text-amber-800">
                                This field is still syncing to the event registration form. Reload the step before connecting it.
                              </div>
                            ) : null}
                          </div>
                        </label>
                        {checked && fieldId && (
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={existing.required}
                              onChange={(e) => {
                                patch({
                                  form_fields: ticketForm.form_fields.map((f) =>
                                    matchesField(f) ? { ...f, required: e.target.checked } : f
                                  ),
                                });
                              }}
                            />
                            <span className="text-[var(--color-text-muted)]">Required for this ticket</span>
                          </label>
                        )}
                      </div>
                      {rowError ? (
                        <p id={`connected-field-error-${field.id}`} className="mt-2 text-xs text-red-700" role="alert">
                          {rowError}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
                {orphanErrors.map((row) => (
                  <div
                    key={`orphan-${row.form_field_id}-${row.index}`}
                    data-field={`form_fields.${row.index}.form_field_id`}
                    data-error-key={`form_fields.${row.index}.form_field_id`}
                    data-form-field-id={row.form_field_id}
                    aria-invalid="true"
                    className="rounded-xl border border-red-400 bg-red-50 px-4 py-3 text-sm ring-1 ring-red-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-soot">Unavailable registration field</div>
                        <p id={`connected-field-error-orphan-${row.index}`} className="mt-1 text-xs text-red-700" role="alert">
                          {row.error}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-800 underline underline-offset-2"
                        onClick={() =>
                          patch({
                            form_fields: ticketForm.form_fields.filter((f) => f.form_field_id !== row.form_field_id),
                          })
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Inventory & limits" description="Quantity caps and sold-out behavior.">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Quantity" required error={fieldErrors.quantity} hint="Total tickets available. Waitlist does not reserve inventory.">
                <input type="number" min={1} className={inputClass} value={ticketForm.quantity} onChange={(e) => patch({ quantity: Number(e.target.value) })} />
              </Field>
              <Field label="Min purchase quantity" error={fieldErrors.min_per_order}>
                <input type="number" min={1} className={inputClass} value={ticketForm.min_per_order} onChange={(e) => patch({ min_per_order: Number(e.target.value) })} />
              </Field>
              <Field label="Max purchase quantity" error={fieldErrors.max_per_order}>
                <input type="number" min={1} className={inputClass} value={ticketForm.max_per_order} onChange={(e) => patch({ max_per_order: Number(e.target.value) })} />
              </Field>
              <Field
                label="Per-user limit"
                error={fieldErrors.max_per_user}
                hint="Maximum tickets one person can buy."
              >
                <input
                  type="number"
                  min={1}
                  className={inputClass}
                  value={ticketForm.max_per_user}
                  onChange={(e) => patch({ max_per_user: Number(e.target.value) })}
                />
              </Field>
              <Field
                label="Sale start"
                error={fieldErrors.sale_start}
                hint="Optional date and time when ticket sales open."
              >
                <DateTimePicker
                  value={ticketForm.sale_start}
                  onChange={(val) => patch({ sale_start: val })}
                  error={fieldErrors.sale_start}
                  placeholder="Select sale start date & time"
                />
              </Field>
              <Field
                label="Sale end"
                error={fieldErrors.sale_end}
                hint="Optional date and time when ticket sales close."
              >
                <DateTimePicker
                  value={ticketForm.sale_end}
                  onChange={(val) => patch({ sale_end: val })}
                  error={fieldErrors.sale_end}
                  placeholder="Select sale end date & time"
                />
              </Field>
              <Field
                label="Sale price (Discounted)"
                error={fieldErrors.sale_price}
                hint="Optional discounted price charged during the sale window (must be less than base price)."
              >
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-xs text-[var(--color-text-muted)] font-semibold">
                    {ticketForm.currency}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    className={`${inputClass} pl-12`}
                    value={ticketForm.sale_price || ''}
                    placeholder="e.g. 50"
                    onChange={(e) => patch({ sale_price: Number(e.target.value) })}
                  />
                </div>
              </Field>
              <Field label="Sold-out behavior" error={fieldErrors.sold_out_behavior}>
                <select
                  className={inputClass}
                  value={ticketForm.sold_out_behavior}
                  onChange={(e) => patch({ sold_out_behavior: e.target.value as TicketFormState['sold_out_behavior'] })}
                >
                  <option value="close_registration">Close registration</option>
                  <option value="enable_waitlist">Enable waitlist</option>
                  <option value="hide_ticket">Hide ticket</option>
                </select>
              </Field>
            </div>

            {(ticketForm.original_price > ticketForm.price || ticketForm.sale_start || ticketForm.sale_end) && (
              <div className="mt-4 rounded-xl border border-amber-200/80 bg-amber-50/60 p-3.5 text-xs text-amber-900 flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-amber-950">
                    <span className="rounded bg-amber-600 px-1.5 py-0.5 text-[10px] text-white">🔥 SALE WINDOW</span>
                    <span>Live Sale Display Preview</span>
                  </div>
                  <p className="text-[#6E7B68]">
                    This ticket will be highlighted with a sale badge and countdown on the public event page.
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  {ticketForm.original_price > ticketForm.price && (
                    <div className="text-sm font-extrabold text-amber-950">
                      <span className="line-through text-slate-400 font-normal mr-1.5">
                        {ticketForm.currency} {ticketForm.original_price.toFixed(2)}
                      </span>
                      {ticketForm.currency} {ticketForm.price.toFixed(2)}
                      <span className="ml-1.5 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] text-white">
                        SAVE {Math.round(((ticketForm.original_price - ticketForm.price) / ticketForm.original_price) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Registration controls" description="Approval, waitlist, and access options.">
            <div className="space-y-3">
              <ToggleRow
                label="Approval required"
                hint="Registrations need organizer approval before confirmation."
                checked={ticketForm.approval_required}
                onChange={(v) => patch({ approval_required: v })}
              />
              <ToggleRow
                label="Waitlist enabled"
                hint="Participants on waitlist pay only during checkout."
                checked={ticketForm.waitlist_enabled}
                onChange={(v) => patch({ waitlist_enabled: v })}
              />
              <ToggleRow label="Allow duplicate purchase" hint="Same participant can buy this ticket multiple times." checked={ticketForm.duplicate_allowed} onChange={(v) => patch({ duplicate_allowed: v })} />
              <ToggleRow
                label="Enable hidden ticket"
                hint="Hide from public listings but allow direct access."
                checked={ticketForm.enable_hidden_ticket}
                onChange={(v) => patch({ enable_hidden_ticket: v, visibility: v ? 'hidden' : ticketForm.visibility })}
              />
              <ToggleRow
                label="Enable invite-only access"
                hint="Restrict purchase to invited attendees with access codes."
                checked={ticketForm.enable_invite_only_access}
                onChange={(v) => patch({ enable_invite_only_access: v, visibility: v ? 'invite_only' : ticketForm.visibility })}
              />
              <ToggleRow label="Refundable" hint="Allow refunds according to your event policy." checked={ticketForm.refundable} onChange={(v) => patch({ refundable: v })} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Communication settings" description="Notifications sent after registration.">
            <div className="space-y-3">
              <ToggleRow label="Confirmation email" hint="Send email when registration completes." checked={ticketForm.communication.confirmation_email} onChange={(v) => patchComm({ confirmation_email: v })} />
              <ToggleRow label="Update notifications" hint="Notify attendees when event details change." checked={ticketForm.communication.update_notifications} onChange={(v) => patchComm({ update_notifications: v })} />
              <ToggleRow label="Show ticket on invoice" hint="Include ticket line item on payment invoice." checked={ticketForm.communication.show_ticket_on_invoice} onChange={(v) => patchComm({ show_ticket_on_invoice: v })} />
              <ToggleRow label="Show QR in confirmation" hint="Embed check-in QR code in confirmation messages." checked={ticketForm.communication.show_qr_in_confirmation} onChange={(v) => patchComm({ show_qr_in_confirmation: v })} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Additional settings" description="Optional settings for advanced ticket customization. Most organizers can leave this unchanged." defaultOpen={false}>
            <div className="space-y-6">
              <Field label="Internal notes">
                <textarea className={textareaClass} value={ticketForm.internal_notes} onChange={(e) => patch({ internal_notes: e.target.value })} />
              </Field>
              <Field label="Ticket tags" hint="Comma-separated labels for internal organization.">
                <input className={inputClass} value={ticketForm.tags} onChange={(e) => patch({ tags: e.target.value })} placeholder="vip, sponsor" />
              </Field>
            </div>
          </CollapsibleSection>

          <div className="flex flex-wrap items-center gap-3 border-t border-nature-border/70 pt-6">
            {(showHighlightedSummary || fieldErrors.ticket) && (
              <p className="w-full rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {fieldErrors.ticket ?? connectedSummary ?? 'Fix the highlighted fields above, then try again.'}
              </p>
            )}
            <button
              type="button"
              onClick={onAddTicket}
              disabled={saving}
              className="rounded-xl bg-soot px-5 py-2.5 text-sm font-medium text-text-inverse hover:bg-soot/90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingTicketId ? 'Save changes' : 'Add ticket'}
            </button>
            {editingTicketId && onCancelEdit && (
              <button
                type="button"
                onClick={onCancelEdit}
                disabled={saving}
                className="rounded-xl border border-nature-border px-5 py-2.5 text-sm font-medium text-soot hover:bg-plaster/50 disabled:opacity-50"
              >
                Cancel edit
              </button>
            )}
          </div>
          </div>
        </WizardPanelBody>
      </WizardPanelCard>
    </div>
  );
}

export { emptyTicketForm };
